import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type UseBodyScrollLock = {
  scrollStatus: boolean;
  toggleScroll(scrollState?: boolean): void;
};

// Global reference counter to track how many components want scroll locked
let scrollLockRefCount = 0;

const toggleLockClasses = (status: boolean) => {
  const bodyElement = document.body;
  const htmlElement = document.documentElement;

  if (bodyElement) {
    bodyElement.classList.toggle("body-locked", status);
    if (htmlElement && bodyElement.scrollHeight > window.innerHeight) {
      htmlElement.classList.toggle("scroll-padding", status);
    }
  }
};

export function useBodyScrollLock(blockScroll = false): UseBodyScrollLock {
  const [scrollStatus, setScrollStatus] = useState(blockScroll);
  const previousScrollStatusRef = useRef<boolean | null>(null);

  useEffect(() => {
    const isInitialMount = previousScrollStatusRef.current === null;
    const shouldLock = (isInitialMount && scrollStatus) || (!previousScrollStatusRef.current && scrollStatus);
    const shouldUnlock = previousScrollStatusRef.current && !scrollStatus;

    if (shouldLock) {
      scrollLockRefCount++;
      toggleLockClasses(true);
      previousScrollStatusRef.current = true;
    } else if (shouldUnlock) {
      // Only remove lock classes if no other components want scroll locked
      if (scrollLockRefCount === 0) {
        toggleLockClasses(false);
      }
      previousScrollStatusRef.current = false;
    }

    // Cleanup on unmount or before effect re-runs
    return () => {
      if (previousScrollStatusRef.current) {
        scrollLockRefCount = Math.max(0, scrollLockRefCount - 1);
        if (scrollLockRefCount === 0) {
          toggleLockClasses(false);
        }
      }
    };
  }, [scrollStatus]);

  const toggleScroll = useCallback((forceState?: boolean) => {
    setScrollStatus((prevState) => forceState ?? !prevState);
  }, []);

  return useMemo(
    () => ({
      scrollStatus,
      toggleScroll,
    }),
    [scrollStatus, toggleScroll]
  );
}

export default useBodyScrollLock;
